import type { Settings } from '../meta/save';
import { CAMERA_ZOOM_OPTIONS, CAMERA_ZOOM_LABELS } from '../core/camera';

/** S11: 'huntboard' is reached directly from 'playing' (the bodega hunt-board interactable), not nested inside the pause flow — its own BACK returns straight to 'playing', mirroring how RESUME (not SETTINGS's BACK) is the only path back to gameplay from the pause screens, just via a different entry point. Wave L-2's 'wardrobe' (the bodega wardrobe stand) follows the exact same shape. Wave I's 'stash' is reachable BOTH ways — a third pause-menu button (`onStateChange('stash')`, same as SETTINGS) AND the bodega counter interactable — "field-accessible anywhere" (PLAN-CITY3 §5, I-3) is the whole point of the pause-menu route. */
export type UiState = 'playing' | 'paused' | 'settings' | 'huntboard' | 'wardrobe' | 'stash';

export interface PauseMenuCallbacks {
  getSettings: () => Settings;
  onSettingsChange: (patch: Partial<Settings>) => void;
  /** A REQUEST to change ui state — main.ts's `setUiState` is the single writer of the actual state; this class only renders whatever it's told via `setVisible`. */
  onStateChange: (state: UiState) => void;
}

/** A labeled settings row: a small text label + one control, laid out as a flex row. Exported (not a private method) so a later session can add its own row into `PauseMenu.settingsBody` without needing a PauseMenu instance. */
export function buildSettingsRow(label: string, control: HTMLElement): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'br3d-settings-row';
  const lab = document.createElement('div');
  lab.className = 'br3d-settings-label';
  lab.textContent = label;
  row.append(lab, control);
  return row;
}

export function buildSlider(min: number, max: number, step: number, value: number, onInput: (v: number) => void): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'range';
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(value);
  input.className = 'br3d-settings-slider';
  input.addEventListener('input', () => onInput(parseFloat(input.value)));
  return input;
}

/**
 * T3-4: a generic N-state cycle button (CLOSE -> NORMAL -> FAR -> CLOSE...)
 * — `buildToggle`'s own boolean-only shape doesn't fit a 3-option setting.
 * Generic over any string-literal union so a future settings row (e.g. a
 * quality-tier control this file's own extension-point comment already
 * anticipates) can reuse it without a copy-pasted 3-state variant.
 */
export function buildCycleButton<T extends string>(options: readonly T[], labels: Record<T, string>, value: T, onChange: (v: T) => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'br3d-settings-toggle';
  let current = value;
  const render = (): void => {
    btn.textContent = labels[current];
  };
  render();
  btn.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    e.preventDefault();
    const idx = options.indexOf(current);
    current = options[(idx + 1) % options.length];
    render();
    onChange(current);
  });
  return btn;
}

export function buildToggle(value: boolean, onChange: (v: boolean) => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'br3d-settings-toggle';
  const render = (v: boolean): void => {
    btn.textContent = v ? 'ON' : 'OFF';
    btn.classList.toggle('on', v);
  };
  render(value);
  btn.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    e.preventDefault();
    const next = !btn.classList.contains('on');
    render(next);
    onChange(next);
  });
  return btn;
}

/**
 * S07's pause flow + settings panel. Two overlay screens stack on top of
 * gameplay: PAUSED (RESUME / SETTINGS) and SETTINGS (the actual controls,
 * with its own BACK button). Only RESUME ever returns to 'playing' —
 * SETTINGS's BACK returns to PAUSED, never straight to gameplay (Classic
 * gotcha #39: a settings screen reachable mid-pause must never let the
 * underlying game silently resume behind it while it's still showing).
 * main.ts owns the single `uiState` source of truth and gates `tick()` on
 * it; this class only renders the two screens and reports state-change
 * REQUESTS via `onStateChange` — it never mutates gameplay state itself.
 */
export class PauseMenu {
  private readonly pauseBtn: HTMLButtonElement;
  private readonly pausedScreen: HTMLDivElement;
  private readonly settingsScreen: HTMLDivElement;
  /** Public extension point — a later session (e.g. a quality-tier control) appends its own `buildSettingsRow(...)` here rather than building a second settings surface. */
  readonly settingsBody: HTMLDivElement;

  constructor(private readonly callbacks: PauseMenuCallbacks) {
    this.pauseBtn = document.createElement('button');
    this.pauseBtn.className = 'br3d-pause-btn';
    this.pauseBtn.textContent = '❚❚';
    this.pauseBtn.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.callbacks.onStateChange('paused');
    });
    document.body.append(this.pauseBtn);

    this.settingsBody = document.createElement('div');
    this.settingsBody.className = 'br3d-settingsscreen-body';

    this.pausedScreen = this.buildPausedScreen();
    this.settingsScreen = this.buildSettingsScreen();
    document.body.append(this.pausedScreen, this.settingsScreen);
    this.setVisible('playing');
  }

  private menuButton(label: string, primary: boolean, onPress: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = primary ? 'br3d-menu-btn br3d-menu-btn-primary' : 'br3d-menu-btn';
    btn.textContent = label;
    btn.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      e.preventDefault();
      onPress();
    });
    return btn;
  }

  private buildPausedScreen(): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'br3d-pausescreen';
    const title = document.createElement('div');
    title.className = 'br3d-pausescreen-title';
    title.textContent = 'PAUSED';
    el.append(
      title,
      this.menuButton('RESUME', true, () => this.callbacks.onStateChange('playing')),
      // Wave I (I-3): "field-accessible anywhere" — the third route into
      // THE STASH, alongside the bodega counter interactable.
      this.menuButton('STASH', false, () => this.callbacks.onStateChange('stash')),
      this.menuButton('SETTINGS', false, () => this.callbacks.onStateChange('settings')),
    );
    return el;
  }

  private buildSettingsScreen(): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'br3d-settingsscreen';
    const title = document.createElement('div');
    title.className = 'br3d-pausescreen-title';
    title.textContent = 'SETTINGS';

    const s = this.callbacks.getSettings();
    const change = (patch: Partial<Settings>): void => this.callbacks.onSettingsChange(patch);

    this.settingsBody.append(
      buildSettingsRow('MUSIC', buildSlider(0, 1, 0.05, s.musicVolume, (v) => change({ musicVolume: v }))),
      buildSettingsRow('SFX', buildSlider(0, 1, 0.05, s.sfxVolume, (v) => change({ sfxVolume: v }))),
      buildSettingsRow('SCREEN SHAKE', buildToggle(s.shakeEnabled, (v) => change({ shakeEnabled: v }))),
      buildSettingsRow('HIT FLASH', buildToggle(s.flashEnabled, (v) => change({ flashEnabled: v }))),
      buildSettingsRow('LEFTY MODE', buildToggle(s.lefty, (v) => change({ lefty: v }))),
      buildSettingsRow('CONTROL SIZE', buildSlider(0.8, 1.3, 0.05, s.controlSize, (v) => change({ controlSize: v }))),
      buildSettingsRow('CAMERA ZOOM', buildCycleButton(CAMERA_ZOOM_OPTIONS, CAMERA_ZOOM_LABELS, s.cameraZoom, (v) => change({ cameraZoom: v }))),
    );

    el.append(title, this.settingsBody, this.menuButton('BACK', false, () => this.callbacks.onStateChange('paused')));
    return el;
  }

  /** Called by main.ts's single `setUiState()` — the only writer of ui state; this method just reflects it into the DOM. */
  setVisible(state: UiState): void {
    this.pausedScreen.style.display = state === 'paused' ? 'flex' : 'none';
    this.settingsScreen.style.display = state === 'settings' ? 'flex' : 'none';
    this.pauseBtn.style.display = state === 'playing' ? 'block' : 'none';
  }
}
